import type { CampaignMetrics } from "@/hooks/api/use-campaign-metrics";

// Props for the ComparisonBadge component
export interface ComparisonBadgeProps {
  currentValue: number | null | undefined;
  benchmarkValue: number | null | undefined;
  metricKey: keyof CampaignMetrics;
  size?: "sm" | "default";
}

/**
 * Helper function to determine if lower is better for a metric
 * For certain metrics like response times, lower values are better
 */
function isLowerBetter(metricKey: string): boolean {
  // For these metrics, a lower value is better
  const lowerIsBetterMetrics = [
    "days_since_first_engagement",
    "days_since_latest_engagement",
  ];

  return lowerIsBetterMetrics.includes(metricKey);
}

/**
 * ComparisonBadge component displays a percentage difference between current value and benchmark
 * with appropriate color coding based on whether the difference is positive or negative
 */
export function ComparisonBadge({
  currentValue,
  benchmarkValue,
  metricKey,
  size = "default", // Default to medium size
}: ComparisonBadgeProps) {
  // Return null if any values are missing or loading
  if (
    typeof currentValue !== "number" ||
    typeof benchmarkValue !== "number" ||
    benchmarkValue === 0
  ) {
    return null;
  }

  // Determine if higher is better for this metric
  const isHigherBetterMetric = !isLowerBetter(metricKey.toString());

  // Calculate difference and percentage
  const diff = currentValue - benchmarkValue;
  const percentDiff = (diff / benchmarkValue) * 100;

  // Determine if the performance is positive based on the metric type
  const isPositivePerformance =
    percentDiff === 0 || // Consider 0% difference as positive
    (isHigherBetterMetric && percentDiff > 0) ||
    (!isHigherBetterMetric && percentDiff < 0);

  // Badge classes for color styling
  const badgeClass = isPositivePerformance
    ? "text-success"
    : "text-destructive";

  // Format percentage - show "0%" for exact match, "<1%" for very small diffs
  const formattedPercent = `${
    percentDiff === 0
      ? "0" // Handle exact zero case first
      : Math.abs(percentDiff) < 1
        ? "<1" // Handle less than 1%
        : Math.abs(percentDiff).toFixed(0) // Handle 1% or more
  }%`;
  const displayPercent =
    percentDiff < 0 ? `-${formattedPercent}` : `+${formattedPercent}`;

  // Adjust size based on the prop
  const sizeClass = size === "sm" ? "text-xs" : "text-sm";

  return (
    <div
      className={`flex items-center uppercase font-medium ${sizeClass} ${badgeClass}`}
    >
      {displayPercent}
    </div>
  );
}

export default ComparisonBadge;
